import type {
  Brand,
  BrandInput,
  Category,
  CategoryInput,
  HeroSlide,
  HeroSlideInput,
  Order,
  OrderHistoryEntry,
  OrderStatus,
  PaginatedOrders,
  PaginatedProducts,
  PaginatedUsers,
  Product,
  ProductInput,
  ProductPriceInput,
  Settings,
  SettingsInput,
  User,
  UserWithStats,
} from '@shop-monorepo/types';
import type { AxiosInstance, AxiosResponse } from 'axios';
import axios from 'axios';

import { API_BASE_URL } from '@/constants/env';

// Class to manage the Axios instance and its interceptors
class AxiosClient {
  public readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Context': 'admin',
      },
      withCredentials: true,
    });

    this.instance.interceptors.request.use((config) => config);

    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.data && error.response.data.error) {
          return Promise.reject(new Error(error.response.data.error));
        }
        return Promise.reject(error);
      },
    );
  }
}

const axiosClient = new AxiosClient().instance;

// API client object with structured methods
export const apiClient = {
  products: {
    all: async (
      page: number = 1,
      limit: number = 10,
      filters?: {
        brand?: string;
        category?: string;
        isActive?: boolean;
        isNew?: boolean;
        q?: string;
      },
      sorter?: { field?: string; direction?: 'asc' | 'desc' },
    ): Promise<PaginatedProducts> => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (filters?.brand) {
        params.append('brand', filters.brand);
      }
      if (filters?.category) {
        params.append('category', filters.category);
      }
      if (filters?.isActive !== undefined) {
        params.append('isActive', String(filters.isActive));
      }
      if (filters?.isNew !== undefined) {
        params.append('isNew', String(filters.isNew));
      }
      if (filters?.q) {
        params.append('q', filters.q);
      }
      if (sorter?.field) {
        params.append('sortBy', sorter.field);
        if (sorter.direction) {
          params.append('sortDir', sorter.direction);
        }
      }
      const response: AxiosResponse<PaginatedProducts> = await axiosClient.get('/products', {
        params,
      });
      return response.data;
    },
    getBySlug: async (slug: string): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosClient.get(`/products/${slug}`);
      return response.data;
    },
    create: async (product: ProductInput): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosClient.post('/products', product);
      return response.data;
    },
    update: async (id: number, product: ProductInput): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosClient.put(`/products/${id}`, product);
      return response.data;
    },
    updateActive: async (id: number, isActive: boolean): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosClient.put(`/products/${id}/active`, {
        isActive,
      });
      return response.data;
    },
    updatePrice: async (id: number, price: ProductPriceInput): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosClient.put(
        `/products/${id}/price`,
        price,
      );
      return response.data;
    },
    delete: async (id: number): Promise<{ message: string }> => {
      const response: AxiosResponse<{ message: string }> = await axiosClient.delete(
        `/products/${id}`,
      );
      return response.data;
    },
    generateDescription: async (name: string): Promise<{ description: string }> => {
      const response = await axiosClient.post('/products/generate-description', { name });
      return response.data;
    },
  },

  brands: {
    all: async (
      sorter?: { field?: string; direction?: 'asc' | 'desc' },
      filters?: { isActive?: boolean },
    ): Promise<Brand[]> => {
      const params = new URLSearchParams();
      if (sorter?.field) {
        params.append('sortBy', sorter.field);
        if (sorter.direction) {
          params.append('sortDir', sorter.direction);
        }
      }
      if (filters?.isActive !== undefined) {
        params.append('isActive', String(filters.isActive));
      }
      const response: AxiosResponse<Brand[]> = await axiosClient.get('/brands', { params });
      return response.data;
    },
    create: async (brand: BrandInput): Promise<Brand> => {
      const response: AxiosResponse<Brand> = await axiosClient.post('/brands', brand);
      return response.data;
    },
    update: async (id: number, brand: BrandInput): Promise<Brand> => {
      const response: AxiosResponse<Brand> = await axiosClient.put(`/brands/${id}`, brand);
      return response.data;
    },
    delete: async (id: number): Promise<{ message: string }> => {
      const response: AxiosResponse<{ message: string }> = await axiosClient.delete(
        `/brands/${id}`,
      );
      return response.data;
    },
  },

  categories: {
    all: async (
      sorter?: { field?: string; direction?: 'asc' | 'desc' },
      filters?: { isActive?: boolean },
    ): Promise<Category[]> => {
      const params = new URLSearchParams();
      if (sorter?.field) {
        params.append('sortBy', sorter.field);
        if (sorter.direction) {
          params.append('sortDir', sorter.direction);
        }
      }
      if (filters?.isActive !== undefined) {
        params.append('isActive', String(filters.isActive));
      }
      const response: AxiosResponse<Category[]> = await axiosClient.get('/categories', { params });
      return response.data;
    },
    create: async (category: CategoryInput): Promise<Category> => {
      const response: AxiosResponse<Category> = await axiosClient.post('/categories', category);
      return response.data;
    },
    update: async (id: number, category: CategoryInput): Promise<Category> => {
      const response: AxiosResponse<Category> = await axiosClient.put(
        `/categories/${id}`,
        category,
      );
      return response.data;
    },
    delete: async (id: number): Promise<{ message: string }> => {
      const response: AxiosResponse<{ message: string }> = await axiosClient.delete(
        `/categories/${id}`,
      );
      return response.data;
    },
  },

  heroSlides: {
    all: async (): Promise<HeroSlide[]> => {
      const response: AxiosResponse<HeroSlide[]> = await axiosClient.get('/hero-slides/admin');
      return response.data;
    },
    create: async (slide: HeroSlideInput): Promise<HeroSlide> => {
      const response: AxiosResponse<HeroSlide> = await axiosClient.post('/hero-slides', slide);
      return response.data;
    },
    update: async (id: number, slide: HeroSlideInput): Promise<HeroSlide> => {
      const response: AxiosResponse<HeroSlide> = await axiosClient.put(`/hero-slides/${id}`, slide);
      return response.data;
    },
    delete: async (id: number): Promise<{ message: string }> => {
      const response: AxiosResponse<{ message: string }> = await axiosClient.delete(
        `/hero-slides/${id}`,
      );
      return response.data;
    },
  },

  upload: {
    file: async (file: File): Promise<{ url: string; thumbnailUrl: string | null }> => {
      const formData = new FormData();
      formData.append('file', file);
      const response: AxiosResponse<{ url: string; thumbnailUrl: string | null }> =
        await axiosClient.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      return response.data;
    },
    delete: async (url: string, thumbnailUrl?: string): Promise<{ success: boolean }> => {
      const response: AxiosResponse<{ success: boolean }> = await axiosClient.delete('/upload', {
        data: { url, thumbnailUrl },
      });
      return response.data;
    },
  },

  orders: {
    all: async (
      page: number = 1,
      limit: number = 10,
      filters?: {
        status?: string;
        isPaid?: boolean;
        isCashed?: boolean;
        deliveryProvider?: string;
      },
      sorter?: { field?: string; direction?: 'asc' | 'desc' },
    ): Promise<PaginatedOrders> => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (filters?.status) {
        params.append('status', filters.status);
      }
      if (filters?.isPaid !== undefined) {
        params.append('isPaid', String(filters.isPaid));
      }
      if (filters?.isCashed !== undefined) {
        params.append('isCashed', String(filters.isCashed));
      }
      if (filters?.deliveryProvider) {
        params.append('deliveryProvider', filters.deliveryProvider);
      }
      if (sorter?.field) {
        params.append('sortBy', sorter.field);
        if (sorter.direction) {
          params.append('sortDir', sorter.direction);
        }
      }
      const response: AxiosResponse<PaginatedOrders> = await axiosClient.get('/orders', { params });
      return response.data;
    },
    getById: async (id: number): Promise<Order> => {
      const response: AxiosResponse<Order> = await axiosClient.get(`/orders/${id}`);
      return response.data;
    },
    updateStatus: async (id: number, status: OrderStatus): Promise<Order> => {
      const response: AxiosResponse<Order> = await axiosClient.put(`/orders/${id}/status`, {
        status,
      });
      return response.data;
    },
    updatePayment: async (id: number, isPaid: boolean): Promise<Order> => {
      const response: AxiosResponse<Order> = await axiosClient.put(`/orders/${id}/payment`, {
        isPaid,
      });
      return response.data;
    },
    updateCash: async (id: number, isCashed: boolean): Promise<Order> => {
      const response: AxiosResponse<Order> = await axiosClient.put(`/orders/${id}/cash`, {
        isCashed,
      });
      return response.data;
    },
    getHistory: async (id: number): Promise<OrderHistoryEntry[]> => {
      const response: AxiosResponse<OrderHistoryEntry[]> = await axiosClient.get(
        `/orders/${id}/history`,
      );
      return response.data;
    },
  },

  users: {
    all: async (
      page = 1,
      limit = 10,
      filters?: { role?: string; hasPassword?: boolean; marketingOptIn?: boolean },
      sorter?: { field?: string; direction?: 'asc' | 'desc' },
    ): Promise<PaginatedUsers> => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (filters?.role) params.append('role', filters.role);
      if (filters?.hasPassword !== undefined)
        params.append('hasPassword', String(filters.hasPassword));
      if (filters?.marketingOptIn !== undefined)
        params.append('marketingOptIn', String(filters.marketingOptIn));
      if (sorter?.field) {
        params.append('sortBy', sorter.field);
        if (sorter.direction) params.append('sortDir', sorter.direction);
      }
      const response: AxiosResponse<PaginatedUsers> = await axiosClient.get('/users', { params });
      return response.data;
    },
    getById: async (id: number): Promise<UserWithStats> => {
      const response: AxiosResponse<UserWithStats> = await axiosClient.get(`/users/${id}`);
      return response.data;
    },
    getOrders: async (userId: number, page = 1, limit = 10): Promise<PaginatedOrders> => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      const response: AxiosResponse<PaginatedOrders> = await axiosClient.get(
        `/users/${userId}/orders`,
        { params },
      );
      return response.data;
    },
  },

  settings: {
    get: async (): Promise<Settings> => {
      const response: AxiosResponse<Settings> = await axiosClient.get('/settings');
      return response.data;
    },
    update: async (data: SettingsInput): Promise<Settings> => {
      const response: AxiosResponse<Settings> = await axiosClient.put('/settings', data);
      return response.data;
    },
  },

  auth: {
    login: async (email: string, password: string): Promise<{ user: User }> => {
      const response: AxiosResponse<{ user: User }> = await axiosClient.post('/auth/login', {
        email,
        password,
      });
      return response.data;
    },
    logout: async (): Promise<{ success: boolean }> => {
      const response: AxiosResponse<{ success: boolean }> = await axiosClient.post('/auth/logout');
      return response.data;
    },
    me: async (): Promise<User> => {
      const response: AxiosResponse<User> = await axiosClient.get('/auth/me');
      return response.data;
    },
    changePassword: async (data: {
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    }): Promise<{ success: boolean }> => {
      const response: AxiosResponse<{ success: boolean }> = await axiosClient.put(
        '/auth/user/password',
        data,
      );
      return response.data;
    },
    forgotPassword: async (email: string): Promise<{ success: boolean }> => {
      const response: AxiosResponse<{ success: boolean }> = await axiosClient.post(
        '/auth/forgot-password',
        { email },
      );
      return response.data;
    },
    resetPassword: async (data: {
      token: string;
      newPassword: string;
      confirmPassword: string;
    }): Promise<{ success: boolean }> => {
      const response: AxiosResponse<{ success: boolean }> = await axiosClient.post(
        '/auth/reset-password',
        data,
      );
      return response.data;
    },
  },
};
